import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { feeApi } from '../utils/api';
import { DollarSign, CreditCard, CheckCircle, Clock, Plus } from 'lucide-react';
import toast from 'react-hot-toast';

interface Fee {
  id: number;
  fee_type: string;
  amount: number;
  semester?: string;
  is_paid: boolean;
  paid_at?: string;
  created_at: string;
}

export default function ClassFees() {
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'teacher';
  const queryClient = useQueryClient();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newFee, setNewFee] = useState({ userId: '', feeType: '', amount: '', semester: '' });

  const { data: fees, isLoading } = useQuery({
    queryKey: ['myFees'],
    queryFn: async () => {
      const response = await feeApi.getMyFees();
      return response.data.fees as Fee[];
    },
  });

  const payMutation = useMutation({
    mutationFn: (id: number) => feeApi.pay(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myFees'] });
      toast.success('班費已繳納');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || '繳費失敗');
    },
  });

  const createMutation = useMutation({
    mutationFn: () => feeApi.create({
      userId: parseInt(newFee.userId) || user!.id,
      feeType: newFee.feeType,
      amount: parseFloat(newFee.amount),
      semester: newFee.semester || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myFees'] });
      toast.success('班費項目已創建');
      setShowCreateForm(false);
      setNewFee({ userId: '', feeType: '', amount: '', semester: '' });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || '創建失敗');
    },
  });

  const unpaidFees = fees?.filter((f) => !f.is_paid) || [];
  const paidFees = fees?.filter((f) => f.is_paid) || [];
  const totalUnpaid = unpaidFees.reduce((sum, f) => sum + f.amount, 0);

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFee.feeType || !newFee.amount) {
      toast.error('請填寫完整資料');
      return;
    }
    createMutation.mutate();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <DollarSign className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">班費管理</h1>
        </div>
        {isAdmin && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            <Plus className="h-4 w-4 mr-1" />
            新增班費
          </button>
        )}
      </div>

      {/* Summary Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="stat bg-error/10 rounded-box">
          <div className="stat-title text-error">待繳金額</div>
          <div className="stat-value text-error">${totalUnpaid.toLocaleString()}</div>
          <div className="stat-desc">{unpaidFees.length} 筆未繳</div>
        </div>
        <div className="stat bg-success/10 rounded-box">
          <div className="stat-title text-success">已繳金額</div>
          <div className="stat-value text-success">
            ${paidFees.reduce((sum, f) => sum + f.amount, 0).toLocaleString()}
          </div>
          <div className="stat-desc">{paidFees.length} 筆已繳</div>
        </div>
        <div className="stat bg-info/10 rounded-box">
          <div className="stat-title text-info">總費用</div>
          <div className="stat-value text-info">
            ${fees?.reduce((sum, f) => sum + f.amount, 0).toLocaleString() || 0}
          </div>
          <div className="stat-desc">{fees?.length || 0} 筆總計</div>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && isAdmin && (
        <div className="card bg-base-100 shadow mb-6">
          <div className="card-body">
            <h3 className="card-title text-lg">新增班費項目</h3>
            <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <input
                type="text"
                className="input input-bordered"
                placeholder="費用類型 (如：班費、活動費)"
                value={newFee.feeType}
                onChange={(e) => setNewFee({ ...newFee, feeType: e.target.value })}
                required
              />
              <input
                type="number"
                className="input input-bordered"
                placeholder="金額"
                value={newFee.amount}
                onChange={(e) => setNewFee({ ...newFee, amount: e.target.value })}
                required
              />
              <input
                type="text"
                className="input input-bordered"
                placeholder="學期 (選填)"
                value={newFee.semester}
                onChange={(e) => setNewFee({ ...newFee, semester: e.target.value })}
              />
              <button
                type="submit"
                className={`btn btn-primary ${createMutation.isPending ? 'loading' : ''}`}
                disabled={createMutation.isPending}
              >
                創建
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Unpaid Fees */}
      <div className="card bg-base-100 shadow mb-6">
        <div className="card-body">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-5 w-5 text-error" />
            <h2 className="card-title">待繳班費</h2>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <span className="loading loading-spinner loading-lg"></span>
            </div>
          ) : unpaidFees.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-2 text-success" />
              <p>無待繳班費，太棒了！</p>
            </div>
          ) : (
            <div className="space-y-3">
              {unpaidFees.map((fee) => (
                <div
                  key={fee.id}
                  className="flex items-center justify-between p-4 bg-error/5 rounded-lg border border-error/20"
                >
                  <div>
                    <p className="font-semibold">{fee.fee_type}</p>
                    {fee.semester && (
                      <p className="text-sm text-gray-500">{fee.semester}</p>
                    )}
                    <p className="text-xs text-gray-400">
                      {new Date(fee.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xl font-bold text-error">${fee.amount.toLocaleString()}</span>
                    <button
                      className={`btn btn-error btn-sm ${payMutation.isPending ? 'loading' : ''}`}
                      onClick={() => payMutation.mutate(fee.id)}
                      disabled={payMutation.isPending}
                    >
                      <CreditCard className="h-4 w-4 mr-1" />
                      繳納
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Paid Fees History */}
      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="h-5 w-5 text-success" />
            <h2 className="card-title">繳費記錄</h2>
          </div>

          {paidFees.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>尚無繳費記錄</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-zebra">
                <thead>
                  <tr>
                    <th>費用類型</th>
                    <th>學期</th>
                    <th className="text-right">金額</th>
                    <th>繳費日期</th>
                  </tr>
                </thead>
                <tbody>
                  {paidFees.map((fee) => (
                    <tr key={fee.id}>
                      <td className="font-medium">{fee.fee_type}</td>
                      <td>{fee.semester || '-'}</td>
                      <td className="text-right text-success font-bold">
                        ${fee.amount.toLocaleString()}
                      </td>
                      <td className="text-sm">
                        {fee.paid_at ? new Date(fee.paid_at).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
